import { useGetGeofenceQuery } from '@casops/store/apis/geofencesApi';
import { ReactElement, useCallback, useEffect } from 'react';
import GeofenceEditor from '../../layers/geofence-editor/GeofenceEditor';
import { Pane } from 'react-leaflet';
import CasGeomanControls from '../cas-geoman-controls/CasGeomanControls';
import { skipToken } from '@reduxjs/toolkit/query';
import { LatLng } from 'leaflet';
import { useAppDispatch } from '@casops/store/hooks';
import { clearGeofenceShape, setGeofenceShape } from '@casops/store/slices/geofenceEditorSlice';
import useShapeEditorOverlay from '../shape-editor-overlay/useShapeEditorOverlay';
import { SHAPE_EDITOR_PANE_NAME, SHAPE_EDITOR_PANE_ZINDEX } from '../../mapConfig';

interface ShapeEditorProps {
  geofenceId?: number;
  createMode: boolean;
}

export const ShapeEditor = ({ geofenceId, createMode }: ShapeEditorProps): ReactElement => {
  const dispatch = useAppDispatch();

  const { currentData: geofence } = useGetGeofenceQuery(
    !createMode && typeof geofenceId === 'number' ? { id: geofenceId } : skipToken,
    { refetchOnMountOrArgChange: 10 },
  );

  useEffect(() => {
    // If in edit mode, set the editor shape slice to match the existing geofence shape when the geofence query has completed.
    if (!createMode && geofence) {
      dispatch(setGeofenceShape(geofence.shape));
    }

    // Clean up the geofence editor shape slice.
    return () => {
      dispatch(clearGeofenceShape());
    };
  }, [dispatch, createMode, geofence]);

  const onShapeUpdated = useCallback(
    (points: LatLng[], radius: number | null) => {
      dispatch(
        setGeofenceShape({
          shape: radius !== null ? 'Circle' : 'Polygon',
          points: points.map(p => [p.lng, p.lat]),
          radius,
        }),
      );
    },
    [dispatch],
  );

  const { overlay, setCircleRadius } = useShapeEditorOverlay({
    initialRadius: geofence?.shape.radius,
  });

  return (
    <>
      <Pane name={SHAPE_EDITOR_PANE_NAME} style={{ zIndex: SHAPE_EDITOR_PANE_ZINDEX }}>
        {!createMode && typeof geofence !== 'undefined' && (
          <GeofenceEditor geofenceShape={geofence.shape} />
        )}
        <CasGeomanControls
          createMode={createMode}
          onCircleRadiusChange={setCircleRadius}
          onShapeUpdated={onShapeUpdated}
        />
      </Pane>
      {overlay}
    </>
  );
};

export default ShapeEditor;
